/* Pumper owns offers, quantities and checkout discounts. This bridge only presents prices. */
(() => {
  'use strict';
  if (!document.querySelector('#pmpr_offer_details')) return;
  const money = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
  const format = cents => money.format(Math.round(cents) / 100);
  const centsFromText = text => {
    const match = text.replace(/\s/g, '').match(/(?:\d{1,3}(?:\.\d{3})+|\d+),\d{2}/);
    return match ? Math.round(Number(match[0].replace(/\./g, '').replace(',', '.')) * 100) : NaN;
  };
  const mounted = new WeakSet();
  let dispose, awaitingCartUntil = 0;
  function mount() {
    const widget = document.querySelector('#prvw__bundle_container');
    const context = document.querySelector('[data-cs-pumper-context]');
    const details = document.querySelector('#pmpr_offer_details');
    if (!widget || !context || !details) return false;
    if (mounted.has(widget)) return true;
    let config;
    try { config = JSON.parse(details.getAttribute('data-prvw_data')).metafieldData; }
    catch { return false; }
    // Mixed-variant bundles, subscriptions and gifts need their own per-line tax calculation.
    if (!config || !Array.isArray(config.quantity) || config.quantity.length === 0) return false;
    if (config.includeVarient || config.subscriptions || config.offerType !== 'quantity breaks') return true;
    const price = context.closest('product-price');
    if (!price) return false;
    const section = price.closest('.shopify-section');
    if (!section || context.dataset.currency !== 'EUR') return true;
    dispose?.();
    mounted.add(widget);
    widget.dataset.csPumperReady = 'true';
    price.querySelectorAll('.cs-pumper-summary').forEach(node => node.remove());
    const summary = document.createElement('div');
    summary.className = 'cs-pumper-summary';
    summary.hidden = true;
    summary.setAttribute('role', 'status');
    summary.setAttribute('aria-live', 'polite');
    summary.setAttribute('aria-atomic', 'true');
    summary.innerHTML = '<div class="cs-pumper-price-pair"><div class="cs-pumper-gross"><strong data-field="gross"></strong><span class="cs-pumper-tax">inkl. 19 % MwSt., zzgl. <a href="/pages/versand-zahlungsbedingungen">Versand</a></span></div><div class="cs-pumper-net"><strong data-field="net"></strong><span>netto gesamt</span></div></div><div class="cs-pumper-details"><p class="cs-pumper-muted" data-field="quantity"></p><p class="cs-pumper-muted" data-field="unit"></p><p class="cs-pumper-muted" data-field="measure"></p><p class="cs-pumper-savings" data-field="savings"></p><p class="cs-pumper-muted" data-field="rrp"></p></div>';
    price.prepend(summary);
    const fields = Object.fromEntries(Array.from(summary.querySelectorAll('[data-field]'), node => [node.dataset.field, node]));
    const set = (key, value) => { if (fields[key].textContent !== value) fields[key].textContent = value; fields[key].hidden = !value; };
    const radios = () => Array.from(widget.querySelectorAll('input.prvw_pair[type="radio"]'));
    widget.querySelector('form')?.setAttribute('aria-label', 'Menge und Staffelrabatt');
    widget.querySelectorAll('img.pmpr-image').forEach(image => { image.alt = ''; image.setAttribute('aria-hidden', 'true'); image.decoding = 'async'; });
    widget.querySelectorAll('h3,h4,h5').forEach(heading => heading.setAttribute('role', 'presentation'));
    const thresholds = [...new Set(config.quantity.map(Number).filter(q => Number.isInteger(q) && q > 0))].sort((a,b) => a-b);
    let requestedQuantity = null, applyTimer = 0;
    const tierFor = q => config.quantity.reduce((best, threshold, i) => Number(threshold) <= q && Number(threshold) >= Number(config.quantity[best] || 0) ? i : best, 0);
    const quantityField = () => section.querySelector('input[name="quantity"][type="number"]');
    let displayedQuantity = Number(quantityField()?.value) || 1;
    // Pumper schedules tier-minimum writes after user selection. Keep this single
    // form input aligned with the latest committed choice, without global patches.
    const valueDescriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
    let guardedInput = null, previousDescriptor;
    function releaseQuantityGuard() {
      if (!guardedInput) return;
      if (previousDescriptor) Object.defineProperty(guardedInput, 'value', previousDescriptor);
      else delete guardedInput.value;
      guardedInput = null;
    }
    function guardQuantity() {
      const input = quantityField();
      if (!input || input === guardedInput) return;
      releaseQuantityGuard();
      guardedInput = input;
      previousDescriptor = Object.getOwnPropertyDescriptor(input, 'value');
      if (previousDescriptor && !previousDescriptor.configurable) { guardedInput = null; return; }
      Object.defineProperty(input, 'value', {
        configurable: true,
        get() { return valueDescriptor.get.call(this); },
        set(value) { valueDescriptor.set.call(this, requestedQuantity !== null ? String(requestedQuantity) : value); }
      });
    }
    guardQuantity();
    function syncCartQuantity(quantity) {
      const current = price.querySelector('[data-cs-pumper-context]');
      const items = window.pmpr_cart_item;
      if (!Number.isInteger(quantity) || quantity < 1 || !current ||
          !Array.isArray(items) || items.length !== 1) return;
      const item = items[0];
      if (String(item.id) !== current.dataset.variantId || item.selling_plan ||
          item.type === 'gift' || item.properties?.pmpr_gift_product !== undefined ||
          item.properties?.pmpr_mix_and_match_product !== undefined) return;
      item.quantity = quantity;
    }
    function onProductSubmit(event) {
      const input = quantityField();
      if (event.target !== input?.form) return;
      awaitingCartUntil = Date.now() + 15000;
      writeQuantity();
      syncCartQuantity(requestedQuantity ?? Number(input.value));
    }
    section.addEventListener('submit', onProductSubmit, true);
    function writeQuantity() {
      guardQuantity();
      const input = quantityField(); if (!input || requestedQuantity === null) return;
      input.value = String(requestedQuantity);
      syncCartQuantity(requestedQuantity);
      input.form?.querySelectorAll('input.pmpr_input[name="quantity"]').forEach(node => { node.value = String(requestedQuantity); });
      // Preserve the exact intermediate quantity. Pumper's change handler otherwise resets to the tier minimum.
      const quantityControl = input.closest('quantity-selector-component');
      const minus = quantityControl?.querySelector('button:first-of-type');
      if (minus) minus.disabled = requestedQuantity <= thresholds[0];
      schedule();
    }
    function chooseQuantity(value) {
      const input = quantityField();
      const min = Number(input?.min) || 1, step = Number(input?.step) || 1;
      const max = Number(input?.max);
      let q = min + Math.max(0, Math.round((value - min) / step)) * step;
      if (max > 0) q = Math.min(q, min + Math.floor((max - min) / step) * step);
      requestedQuantity = q;
      const radio = radios()[tierFor(q)];
      if (radio && !radio.checked) radio.click();
      writeQuantity();
      clearTimeout(applyTimer); applyTimer = setTimeout(writeQuantity, 180);
    }
    const onManualQuantity = event => {
      if (!event.isTrusted || event.target !== quantityField() || !Number(event.target.value)) return;
      event.stopImmediatePropagation();
      chooseQuantity(Number(event.target.value), true);
    };
    const onQuantityFocus = event => {
      if (event.target !== quantityField()) return;
      clearTimeout(applyTimer);
      requestedQuantity = null;
      releaseQuantityGuard();
    };
    section.addEventListener('focusin', onQuantityFocus);
    section.addEventListener('input', onManualQuantity, true);
    section.addEventListener('change', onManualQuantity, true);
    const onTierClick = event => {
      if (!event.isTrusted || !event.target.closest('label,input.prvw_pair')) return;
      const label = event.target.closest('label');
      const radio = label ? document.getElementById(label.htmlFor) : event.target.closest('input.prvw_pair');
      const index = radios().indexOf(radio);
      if (index < 0) return;
      requestedQuantity = Number(config.quantity[index]);
      clearTimeout(applyTimer);
      applyTimer = setTimeout(writeQuantity, 180);
      schedule();
    };
    widget.addEventListener('click', onTierClick, true);
    const onQuantityButton = event => {
      const input = quantityField();
      const control = input?.closest('quantity-selector-component');
      const button = event.target.closest('button');
      if (button?.matches('button[name="add"]') && button.closest('form') === input?.form) {
        awaitingCartUntil = Date.now() + 15000;
        writeQuantity();
        return;
      }
      if (!button || !control?.contains(button)) return;
      const direction = button === control.querySelector('button:first-of-type') ? -1 : 1;
      event.preventDefault(); event.stopImmediatePropagation();
      const currentQuantity = requestedQuantity ?? displayedQuantity;
      const nextQuantity = Math.max(Number(input.min) || 1, currentQuantity + direction);
      const actualMax = Number(input.max);
      chooseQuantity(actualMax > 0 ? Math.min(actualMax, nextQuantity) : nextQuantity, true);
    };
    section.addEventListener('click', onQuantityButton, true);

    let frame = 0, settle = 0;
    function refresh() {
      frame = 0;
      const current = price.querySelector('[data-cs-pumper-context]');
      const inputs = radios();
      const quantityInput = section.querySelector('input[name="quantity"][type="number"]');
      if (requestedQuantity !== null && quantityInput) {
        if (Number(quantityInput.value) !== requestedQuantity) quantityInput.value = String(requestedQuantity);
        quantityInput.form?.querySelectorAll('input.pmpr_input[name="quantity"]').forEach(node => { node.value = String(requestedQuantity); });
      }
      const quantity = requestedQuantity ?? Number(quantityInput?.value);
      const index = Number.isInteger(quantity) && quantity > 0 ? tierFor(quantity) : inputs.findIndex(input => input.checked);
      if (Number.isInteger(quantity) && quantity > 0) displayedQuantity = quantity;
      syncCartQuantity(quantity);
      section.querySelectorAll('product-inventory').forEach(node => node.updateQuantity?.(quantity));
      const amount = widget.querySelector('#prvw_totalAmount_' + index);
      const displayed = centsFromText(amount?.textContent || '');
      const divisor = Number(current?.dataset.divisor);
      const base = Number(current?.dataset.unitPrice);
      const tierQuantity = Number(config.quantity?.[index]);
      const perUnit = config.showPriceByEach === true;
      // Fall back to Shopify's unmodified price if the app is absent or its format is unknown.
      const valid = index >= 0 && current && divisor === 1.19 && Number.isInteger(quantity) && quantity > 0 && Number.isFinite(displayed) && displayed >= 0 && base > 0 && (perUnit || quantity === tierQuantity);
      summary.hidden = !valid;
      price.classList.toggle('cs-pumper-active', Boolean(valid));
      if (!valid) return;
      const quantityControl = quantityInput.closest('quantity-selector-component');
      const minusButton = quantityControl?.querySelector('button[name="minus"]');
      const plusButton = quantityControl?.querySelector('button[name="plus"]');
      if (minusButton) minusButton.disabled = quantity <= thresholds[0];
      if (plusButton) plusButton.disabled = Number(quantityInput.max) > 0 && quantity >= Number(quantityInput.max);
      let gross = perUnit ? displayed * quantity : displayed;
      const original = base * quantity;
      const saved = Math.max(0, original - gross);
      const uvp = Number(current.dataset.comparePrice);
      set('quantity', '');
      set('net', format(gross / divisor));
      set('gross', format(gross));
      set('unit', quantity > 1 ? 'Je Einheit: ' + format(gross / quantity) + ' brutto / ' + format(gross / quantity / divisor) + ' netto' : '');
      set('savings', saved > 0 ? format(saved) + ' gespart gegenüber dem Einzelkauf (brutto)' : '');
      const measurePrice = Number(current.dataset.measurePrice);
      const measureRef = (current.dataset.measureRef || '').trim();
      const duplicateItemPrice = /^1\s+item$/i.test(measureRef) && Math.abs(measurePrice - base) < 1;
      set('measure', measurePrice > 0 && !duplicateItemPrice ? 'Grundpreis: ' + format(measurePrice * gross / original) + ' / ' + measureRef.replace(/\bitem\b/gi, 'Stück') + ' inkl. MwSt.' : '');
      const rrpText = uvp > base ? format(uvp) + ' UVP je Verkaufseinheit inkl. MwSt.' : '';
      if (fields.rrp.textContent !== rrpText) {
        fields.rrp.replaceChildren();
        if (rrpText) { const strike = document.createElement('s'); strike.textContent = format(uvp); fields.rrp.append(strike, ' UVP je Verkaufseinheit inkl. MwSt.'); }
      }
      fields.rrp.hidden = !rrpText;
      summary.dataset.discounted = String(saved > 0 || uvp > base);
      inputs.forEach((input, i) => {
        const label = widget.querySelector('label[for="' + input.id + '"]');
        if (document.documentElement.lang === 'de') {
          const amount = widget.querySelector('#prvw_totalAmount_' + i + ' .dualPrice') || widget.querySelector('#prvw_totalAmount_' + i);
          if (amount) for (const node of amount.childNodes) if (node.nodeType === Node.TEXT_NODE) {
            const formatted=node.textContent.replace(/^\s*€\s*([\d.]+,\d{2})\s*$/, '$1 €');
            if (formatted !== node.textContent) node.textContent=formatted;
          }
        }
        const title = config.title?.[i] || '';
        const labelText = title + ', ' + config.quantity[i] + ' Verkaufseinheiten';
        if (input.getAttribute('aria-label') !== labelText) input.setAttribute('aria-label', labelText);
        const right = label?.querySelector('.template_4_block__cbmain--content--right');
        if (perUnit && right && !right.querySelector('.cs-pumper-unit-label')) {
          const note = document.createElement('span'); note.className = 'cs-pumper-unit-label'; note.textContent = 'brutto je Einheit'; right.append(note);
        }
      });
    }
    function schedule() { if (!frame) frame = requestAnimationFrame(refresh); }
    function onInput(event) {
      if (!widget.contains(event.target) && !event.target.matches?.('input[name="quantity"],input[type="radio"],select')) return;
      
      schedule();
      clearTimeout(settle);
      settle = setTimeout(schedule, 150); // Let Pumper finish its own quantity/variant synchronization.
    }
    section.addEventListener('change', onInput);
    section.addEventListener('input', onInput);
    widget.addEventListener('click', onInput);
    const observer = new MutationObserver(schedule);
    observer.observe(widget, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['style', 'class', 'checked'] });
    const priceObserver = new MutationObserver(records => {
      if (records.some(record => record.target === price)) schedule();
    });
    priceObserver.observe(price, { childList: true });
    dispose = () => {
      observer.disconnect(); priceObserver.disconnect();
      section.removeEventListener('submit', onProductSubmit, true);
      section.removeEventListener('change', onInput); section.removeEventListener('input', onInput);
      widget.removeEventListener('click', onInput);
      widget.removeEventListener('click', onTierClick, true);
      section.removeEventListener('click', onQuantityButton, true);
      section.removeEventListener('focusin', onQuantityFocus);
      section.removeEventListener('input', onManualQuantity, true);
      section.removeEventListener('change', onManualQuantity, true);
      clearTimeout(applyTimer); releaseQuantityGuard();
      cancelAnimationFrame(frame); clearTimeout(settle); summary.remove();
      price.classList.remove('cs-pumper-active');
    };
    refresh();
    return true;
  }
  document.addEventListener('cart:update', async event => {
    const cart = event.detail?.resource;
    if (!cart || !Array.isArray(cart.items) || Date.now() > awaitingCartUntil) return;
    awaitingCartUntil = 0;
    try {
      const { CartLinesUpdateEvent } = await import('@shopify/events');
      document.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add', context: 'product', lines: [],
        promise: Promise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source: 'clendo-pumper', itemCount: cart.item_count }
        })
      }));
    } catch (error) { console.warn('Clendo: Warenkorbanzeige konnte nicht aktualisiert werden.', error); }
  });
  mount();
  // Pumper can replace its server-rendered widget during initialization or variant changes.
  const boot = new MutationObserver(records => {
    if (records.some(record => Array.from(record.addedNodes).some(node => node.nodeType === 1 &&
      (node.matches('#prvw__bundle_container,product-price') || node.querySelector('#prvw__bundle_container,product-price'))))) mount();
  });
  boot.observe(document.getElementById('MainContent'), { childList: true, subtree: true });
})();



